import { useMemo } from 'react';

import './resizers.scss';
import useGridResizer, {
  UseGridResizerProps,
} from '../../../hooks/useGridResizer';
import RowResizer from './RowResizer';
import ColumnResizer from './ColumnResizer';
import { GridEntity } from '../../../types';
import { getColumnsCellView } from '../../../helpers/cellView/cellColumns';
import {
  ColumnCellView,
  RowCellView,
} from '../../../helpers/cellView/cellViewTypes';
import { getRowsCellView } from '../../../helpers/cellView/cellRows';

const Resizers: React.FC<UseGridResizerProps> = (props) => {
  const { onResizerMouseDown, lineSize, linePos, selectedLine } =
    useGridResizer(props);

  const {
    sheetId,
    // viewport,
    // groupBarHeight,
    colHeaderHeight,
    isRtl,
    stroke,

    zoom,
    rowViewport,
    colViewport,
    // filterResults,

    selectionBorder,
  } = props;

  const lineHalf = lineSize / 2;

  // #region views
  const moveableLineView = () => {
    const isCol = selectedLine === GridEntity.Column;
    const x = isCol ? linePos : 0;
    const y = isCol ? props.y : linePos; // + props.groupBarHeight
    const width = isCol ? lineSize : props.width;
    const height = isCol ? props.height : lineSize;
    return (
      <div
        style={{
          width: `${width}px`,
          height: `${height}px`,
          left: `${x}px`,
          top: `${y}px`,
          background: props.selectionBorder,
          position: 'absolute',
          opacity: 0.3,
          zIndex: 2,
        }}
      />
    );
  };

  const renderColResizer = (params: ColumnCellView) => {
    const { x, width, ci } = params;
    const linePos = isRtl ? x - lineHalf : x + width - lineHalf;
    const edgePos = isRtl ? x : x + width;
    return (
      <ColumnResizer
        {...params}
        linePos={linePos}
        sheetId={sheetId}
        gridX={props.x}
        gridWidth={props.width}
        lineSize={lineSize}
        selectionBorder={selectionBorder}
        stroke={stroke}
        isRtl={isRtl}
        colHeaderHeight={colHeaderHeight}
        // groupBarHeight={groupBarHeight}
        zoom={zoom}
        edgePos={edgePos}
        onResizerMouseDown={onResizerMouseDown}
        key={`${sheetId}-col-resizer-${ci}`}
      />
    );
  };

  const renderRowResizer = (params: RowCellView) => {
    const linePos = params.y + params.height - lineHalf;
    const key = `${sheetId}-row-resizer-${params.ri}`;
    return (
      <RowResizer
        {...params}
        linePos={linePos}
        sheetId={sheetId}
        gridX={props.x}
        gridWidth={props.width}
        gridY={props.y}
        gridHeight={props.height}
        rowHeaderWidth={props.rowHeaderWidth}
        lineSize={lineSize}
        selectionBorder={selectionBorder}
        isRtl={isRtl}
        zoom={zoom}
        stroke={stroke}
        key={key}
        onResizerMouseDown={onResizerMouseDown}
      />
    );
  };

  // #endregion

  const { ColumnsResizers, RowResizers } = useMemo(
    () => ({
      ColumnsResizers: getColumnsCellView(props, renderColResizer, {
        checkHiddenColumns: true,
      }),
      RowResizers: getRowsCellView(props, renderRowResizer, {
        checkHiddenRows: true,
      }),
    }),
    [
      props.x,
      props.y,
      props.height,
      props.width,
      sheetId,
      rowViewport,
      colViewport,
      isRtl,
      stroke,

      lineSize,
      lineHalf,

      zoom,
    ]
  );

  return (
    <>
      {ColumnsResizers}
      {RowResizers}
      {selectedLine !== 'none' && moveableLineView()}
    </>
  );
};

export default Resizers;
